import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

// Markdown Imports
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// Icons
import BarChartIcon from "@mui/icons-material/BarChart";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import TableChartIcon from "@mui/icons-material/TableChart";
import TuneIcon from "@mui/icons-material/Tune"; // New Icon for Hover State

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884d8",
  "#82ca9d", // old standard
  "#a05195",
  "#665191",
  "#2f4b7c",
  "#f95d6a",
  "#ff7c43",
  "#ffa600", // New Standard
  "#4BC0C0",
  "#FF6384",
  "#36A2EB",
  "#FF9F40",
  "#9966FF",
  "#FFCD56", // Pastel
  "#C9CBCF",
  "#537bc4",
  "#acc236",
  "#166a8f",
  "#00a950",
  "#58595b", // Extra
  "#8549ba",
];

const UniversalChart = ({ data, title, dataKey, rawMarkdown }) => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const [chartType, setChartType] = useState("table");

  // State for Hover Effect on Icons
  const [isHovered, setIsHovered] = useState(false);

  const customMarkdownComponents = {
    table: ({ node, ...props }) => (
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          my: 0,
          borderRadius: 0,
          bgcolor: "transparent",
          width: "100%",
          minWidth: "100%",
          "&::-webkit-scrollbar": { width: "2px", height: "2px" },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor:
              mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.1)",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor:
              mode === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.2)",
          },
        }}
      >
        <Table
          size="small"
          aria-label="markdown table"
          stickyHeader
          {...props}
        />
      </TableContainer>
    ),

    thead: ({ node, ...props }) => (
      <TableHead
        sx={{
          bgcolor:
            mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
          whiteSpace: "nowrap",
          fontFamily: "Arial, sans-serif",
          fontSize: "0.75rem",
        }}
        {...props}
      />
    ),

    tbody: ({ node, ...props }) => <TableBody {...props} />,

    tr: ({ node, ...props }) => (
      <TableRow
        sx={{
          "&:last-child td, &:last-child th": { border: 0 },
          whiteSpace: "nowrap",
          fontFamily: "Arial, sans-serif",
        }}
        {...props}
      />
    ),

    th: ({ node, ...props }) => (
      <TableCell
        align="left"
        sx={{
          fontFamily: "Arial, sans-serif",
          fontWeight: "bold",
          color: "text.primary",
          fontSize: "0.75rem",
          p: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          whiteSpace: "nowrap",
          bgcolor: mode === "dark" ? "#1e1e1e" : "#fff",
          fontFamily: "Arial, sans-serif",
        }}
        {...props}
      />
    ),
    td: ({ node, ...props }) => (
      <TableCell
        align="left"
        sx={{
          color: "text.secondary",
          fontSize: "0.75rem",
          p: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          whiteSpace: "nowrap",
          fontFamily: "Arial, sans-serif",
        }}
        {...props}
      />
    ),
    a: ({ node, ...props }) => (
      <a {...props} style={{ color: "#1565c0", textDecoration: "underline" }} />
    ),
  };

  if (!data || data.length === 0) return null;

  const renderContent = () => {
    const minWidthPerDataPoint = 60;
    const dynamicWidth = data.length * minWidthPerDataPoint;

    const isScrollable = dynamicWidth > 200;

    switch (chartType) {
      case "table":
        return (
          <Box sx={{ height: "auto", p: 1, fontFamily: "Arial, sans-serif" }}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={customMarkdownComponents}
            >
              {rawMarkdown || ""}
            </ReactMarkdown>
          </Box>
        );

      case "line":
        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              overflowX: "auto",
              overflowY: "hidden",
            }}
          >
            <Box
              sx={{
                width: isScrollable ? dynamicWidth : "100%",
                height: "100%",
                minWidth: "100%",
              }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={data}
                  margin={{ top: 10, right: 30, left: -20, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 10 }}
                    tickFormatter={(val) => val.split("__")[0].split(",")[0]}
                    interval={0}
                    angle={isScrollable ? -45 : 0}
                    textAnchor={isScrollable ? "end" : "middle"}
                    height={isScrollable ? 60 : 30}
                  />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      borderRadius: 8,
                    }}
                    labelFormatter={(label) => label.split("__")[0]}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: "12px" }}
                    sx={{ fontFamily: "Arial, sans-serif" }}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    name={dataKey || "Value"}
                    stroke={theme.palette.primary.main}
                    strokeWidth={1}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        );
      case "pie":
        return (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
                sx={{ fontFamily: "Arial, sans-serif" }}
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: 8,
                  color: theme.palette.text.primary,
                  border: `1px solid ${theme.palette.divider}`,
                }}
                itemStyle={{
                  color: theme.palette.text.primary,
                }}
                formatter={(value, name) => [value, dataKey || "Value"]}
                sx={{ fontFamily: "Arial, sans-serif" }}
              />
              <Legend wrapperStyle={{ fontSize: "12px" }} />
            </PieChart>
          </ResponsiveContainer>
        );

      default:
        return (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              overflowX: "auto",
              overflowY: "hidden",
            }}
          >
            <Box
              sx={{
                width: isScrollable ? dynamicWidth : "100%",
                height: "100%",
                minWidth: "100%",
              }}
            >
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data}
                  margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                  sx={{ fontFamily: "Arial, sans-serif" }}
                  // barCategoryGap={isScrollable ? "0%" : "0%"}
                  barCategoryGap={isScrollable ? "2%" : "5%"}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#eee"
                  />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 10 }}
                    tickFormatter={(val) => val.split("__")[0].split(",")[0]}
                    interval={0}
                    angle={isScrollable ? -45 : 0}
                    textAnchor={isScrollable ? "end" : "middle"}
                    height={isScrollable ? 60 : 30}
                  />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip
                    cursor={{ fill: "transparent" }}
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      borderRadius: 8,
                    }}
                    labelFormatter={(label) => label.split("__")[0]}
                  />
                  <Legend wrapperStyle={{ fontSize: "12px" }} />
                  <Bar
                    dataKey="value"
                    name={dataKey || "Value"}
                    fill={theme.palette.primary.main}
                    radius={[4, 4, 0, 0]}
                    // barSize={isScrollable ? 40 : undefined}
                    barSize={isScrollable ? 10 : 10}
                  >
                    {data.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        );
    }
  };

  return (
    <Paper
      elevation={2}
      sx={{
        p: 0,
        m: 0,
        width: "100%",
        maxWidth: 600,
        borderRadius: 2,
        bgcolor: theme.palette.mode === "dark" ? "#1e1e1e" : "#fff",
        color: theme.palette.text.primary,
        overflow: "hidden",
      }}
    >
      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        sx={{
          p: 1,
          height: 40,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor:
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.05)"
              : "rgba(0,0,0,0.02)",
        }}
      >
        <Box
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            transition: "all 0.3s ease",
            borderRadius: 1,
            cursor: "pointer",
          }}
        >
          {!isHovered && (
            <TuneIcon
              sx={{
                fontSize: 18,
                color: theme.palette.text.secondary,
                m: 0.5,
              }}
            />
          )}

          {/* Show Buttons when Hovered */}
          {isHovered && (
            <ToggleButtonGroup
              value={chartType}
              exclusive
              onChange={(e, newType) => newType && setChartType(newType)}
              size="small"
              sx={{
                height: 24,
                "& .MuiToggleButton-root": {
                  border: "none",
                  borderRadius: 1,
                  mx: 0.2,
                  padding: "2px 6px",
                },
                "& .Mui-selected": {
                  bgcolor: theme.palette.primary.main + " !important",
                  color: "#fff !important",
                },
              }}
            >
              <ToggleButton
                value="table"
                aria-label="table view"
                title="View Table"
                sx={{ fontFamily: "Arial, sans-serif" }}
              >
                <TableChartIcon sx={{ fontSize: 16 }} />
              </ToggleButton>
              <ToggleButton
                value="bar"
                aria-label="bar chart"
                title="Bar Chart"
              >
                <BarChartIcon sx={{ fontSize: 16 }} />
              </ToggleButton>
              <ToggleButton
                value="line"
                aria-label="line chart"
                title="Line Chart"
              >
                <ShowChartIcon sx={{ fontSize: 16 }} />
              </ToggleButton>
              {/* <ToggleButton
                value="pie"
                aria-label="pie chart"
                title="Pie Chart"
              >
                <PieChartIcon sx={{ fontSize: 16 }} />
              </ToggleButton> */}
            </ToggleButtonGroup>
          )}
        </Box>
      </Box>

      {/* Main Content Area */}
      <Box sx={{ width: "100%", height: chartType === "table" ? "auto" : 300 }}>
        {renderContent()}
      </Box>
    </Paper>
  );
};

export default UniversalChart;
